import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import { useScreenDetector } from '../custom-hooks/screen-detector';

import CompactLayout from './CrossSell/Layouts/Compact';
import LargeImageLayout from './CrossSell/Layouts/LargeImage';
import LineLayout from './CrossSell/Layouts/Line';
import LineVerticalLayout from './CrossSell/Layouts/LineVertical';
import CheckboxLayout from './CrossSell/Layouts/Checkbox';
import TileLayout from './CrossSell/Layouts/Tile';
import FBT from './FBT/Layouts/Default';
import { logger } from '../../../common/logger';

import {
  calculateDiscountValue,
  getAmountDisplay,
  getDiscountDisplay,
} from '../helpers/discount';
import { roundOff, roundTo2String } from '../helpers/float';
import { getSeparatedStyleValueArr } from '../helpers/style';
import { reloadPage } from '../../../pageHelper';

// helpers
const addedVariantsPrefixMap = {
  'paaduks.myshopify.com': '',
  'ikeg-com-br.myshopify.com': '',
};

const getLayoutType = ({
  isMobile, // isMobile and isAdmin Mobile tab is already indentified in this new component
  mobileLayoutType,
  desktopLayoutType,
}) => {
  if (isMobile) return mobileLayoutType;
  return desktopLayoutType;
};
// helpers

export default function Embedded(props) {
  const {
    isConsiderReservedQuantity,
    reservedItemQuantity,
    isRepeatRules,
    isAdmin,
    isAdminMobileTab,
    adminShowLargePreview,
    mrOfferID,
    shopURL,
    offerType,
    products,
    calculateDiscountAdmin,
    offTextAdmin,
    triggerType,
    eventConstants,
    sendEvent,
    cart,
    primary,
    productAdditions,
    balanceProductAddtions,
    isHideAfterProductAddition,
    variantIDsOfProductsToHide,
    addVariantIdsOfProductsToHide,
    discountQuantity,
    isDiscountQuantityRestricted,
    isNumberOfOfferedProductsRestricted,
    goToProductPage,
    goToCart,
    getMonkDiscountProperties,
    variantsWithQuantity,
    changeCartVartiantWithoutProperties,
    selectedCheckboxProducts,
    setSelectedCheckboxProducts,
    addProductAddonsToCart,
    currencyFormat,
    currencySymbol,
    thousandSeparator,
    decimalSymbol,
    discountType,
    discountRoundType,
    priceRoundType,
    fbtDiscountValue,
    fbtPartialDiscountValue,
    isFBTDiscountDisabled,
    isFBTPartialDiscountDisabled,
    addOfferedVariantsToCart,
    addToCartSuccessEvent,
    _compareCartAndAddProductAdditions,
    _getVariantQuantity,
    _getVariantQuantityFromCart,
    _getProductQuantity,
    _isPrimaryAndOfferedSame,
    _getProductAddtionsByProductIndex,
    _isVariantPresentInCart,
    _onAddClick,
    _productEvent,
    _getProductFromVariantID,
    _getLastAddition,
    _getLastAddedVariant,
    _getAddedVariantsToDisplay,
    _getProductIDFromVariantID,
    _getBundleOrAddonOrFBTVariantsToAdd,
    offerLevelDiscountQuantity,
    atcStatus,
    widgetID,
    ...restProps // only rest props will be passed in the child components.
  } = props;

  /**
   * Here we clone the theme and update values based on the requirements.
   * the getConstrainedTheme function returns a cloned version of the theme object with constrained values based on the trigger type and offer type.
   */
  const getConstrainedTheme = () => {
    const themeClone = cloneDeep(props.theme);
    const themeKeyValueReplacements = {
      product_page: {
        freegift: {
          offer_header_text: 'pdpFgOfferHeaderText',
        },
      },
    };
    let constrainedTheme = {};
    if (
      themeKeyValueReplacements[props.triggerType] &&
      themeKeyValueReplacements[props.triggerType][offerType]
    ) {
      constrainedTheme =
        themeKeyValueReplacements[props.triggerType][offerType];
    }
    // the themeClone object will be updated based on the constrainedTheme object.
    Object.keys(constrainedTheme).forEach(elem => {
      themeClone[elem] =
        props.theme[
          themeKeyValueReplacements[props.triggerType][offerType][elem]
        ];
    });
    return themeClone;
  };

  const {
    mobileLayoutType,
    desktopLayoutType,
    isShowPriceInFreeGift,
    isVariantSelectionDisabled,
    isProductButtonActionGoToPage,
    isBottomBarButtonActionGoToCart,
    isSelectedVariantItemsDisabled,
    isToggleCheckboxOnImageClick,
    isEmbeddedMobileCarouselEnabled,
    isEmbeddedCarouselEnabled,
    freeText,
    isProductTitleClickGoToPage,
    isProductImageClickGoToPage,

    // widget styles
    font: widgetFontFamily,
    offerBackgroundColor: widgetBackgroundColor,
    isOfferBordered: isWidgetBordered,
    offerBorderWidth: widgetBorderWidth,
    offerBorderColor: widgetBorderColor,
    widgetBorderRadius,
    widgetPadding,
    widgetMargin,
    widgetMobilePadding,
    widgetMobileMargin,

    // for largeImage ProductWidth calculation
    productButtonWidth,
    productButtonMobileWidth,
    productVariantWidth,
    productVariantMobileWidth,
    isCarouselArrowsAlwaysVisible,
  } = props.theme;

  const { isMobile: isMobileDevice } = useScreenDetector();

  const {
    EventActionCartPage,
    EventActionProductPage,
    EventActionOpen,
    EventActionSelect,
    EventNameOfferPageLaunch,
    EventNameOfferProductPage,
    EventNameProductButtonClick,
    // EventVariableDefault,
    // EventVariableVariant,
    EventVariableVariant,
    EventVariableDefault,
  } = eventConstants;

  const isProductPage = triggerType === 'product_page';
  const isFreegiftOffer = offerType === 'freegift';
  const isFBTOffer = offerType === 'fbt';
  const isPdpFg = isFreegiftOffer && isProductPage;
  const isPdpFgWithRepetetionOfRules = isPdpFg && isRepeatRules;

  // Main container ref (used for calculating carousel width)
  const mainContainerRef = useRef(null);

  const isMobile = isAdmin ? isAdminMobileTab : isMobileDevice;

  const layoutType = getLayoutType({
    isMobile,
    mobileLayoutType,
    desktopLayoutType,
  });

  // carousel items to show in one slide
  const [carouselItemsToShow, setCarouselItemsToShow] = useState(1);

  const [
    isProductAddonActionLoading,
    setIsProductAddonActionLoading,
  ] = useState(false);
  const [isProductAddonAdded, setIsProductAddonAdded] = useState(false);

  // Display Products is used to hide added products from offer on client side
  const [displayProducts, setDisplayProducts] = useState(
    products.map((product, i) => ({ ...product, originalIndex: i })),
  );
  // Selected variants for all products(including client-side hidden products)
  const [selectedVariantInfo, setSelectedVariantInfo] = useState(
    products.reduce(
      (acc, curr, idx) => acc.concat(products[idx].variants[0]),
      [],
    ),
  );

  const [mainContainerWidth, setMainContainerWidth] = useState(0);

  // FBT specific states
  const [isFBTActionLoading, setIsFBTActionLoading] = useState(false);
  const [isFBTProductsAdded, setIsFBTProductsAdded] = useState(false);
  const [fbtCheckedProductsInfo, setFBTCheckedProductsInfo] = useState(
    products.map(() => true),
  );

  const [fbtImagesArray, setFBTImagesArray] = useState([]);
  const [fbtTotal, setFBTTotal] = useState({
    priceFloat: 0,
    offerPriceFloat: 0,
    discountFloat: 0,
    displayDiscount: false,
  });
  // FBT specific states

  const embeddedSendEvent = eventData => {
    sendEvent({ ...eventData, isEmbedded: true, isProductPage, offerType });
  };

  const productEvent = eventData =>
    _productEvent({
      eventData,
      primary,
      products,
      sendEvent: embeddedSendEvent,
    });

  const getProductAddtionsByProductIndex = idx =>
    _getProductAddtionsByProductIndex({
      idx,
      products,
      productAdditions,
    });

  // checkbox layout helper
  const getIndexInSelectedCheckboxProducts = idx =>
    selectedCheckboxProducts.findIndex(item => item.originalIndex === idx);

  const onAddProductToAddon = (product, selectedVariant) => {
    const newSelectedCheckboxProducts = [...selectedCheckboxProducts];
    const addedProductAndVariant = { ...product, variants: [selectedVariant] };
    const index = getIndexInSelectedCheckboxProducts(product.originalIndex);
    if (index > -1) {
      newSelectedCheckboxProducts.splice(index, 1);
    } else {
      newSelectedCheckboxProducts.push(addedProductAndVariant);
    }

    setSelectedCheckboxProducts(newSelectedCheckboxProducts);
  };

  const onAddProductAddonsToCart = async () => {
    if (isAdmin) return;

    setIsProductAddonActionLoading(true);

    await addProductAddonsToCart({
      mrOfferID,
      selectedCheckboxProducts: [
        ...selectedCheckboxProducts,
        {
          id: primary.sp_product_id,
          variants: [{ id: primary.sp_variant_id }],
        },
      ],
      theme: props.theme,
      dispatch: props.dispatch,
      isAboveATC: false,
      widgetID,
    });
    setIsProductAddonActionLoading(false);
    setIsProductAddonAdded(true);
    setTimeout(() => {
      setIsProductAddonAdded(false);
    }, 2000);
  };
  // checkbox layout helper

  const onSelectVariant = (idx, variant, isUserSelected) => {
    // maintain selected variant for all products
    const newSelectedVariantInfo = [...selectedVariantInfo];
    newSelectedVariantInfo[idx] = variant;
    setSelectedVariantInfo(newSelectedVariantInfo);

    if (layoutType === 'checkbox') {
      const checkboxProductIndex = getIndexInSelectedCheckboxProducts(idx);
      if (checkboxProductIndex > -1) {
        const newSelectedCheckboxProducts = [...selectedCheckboxProducts];
        newSelectedCheckboxProducts[checkboxProductIndex].variants = [variant];
        setSelectedCheckboxProducts(newSelectedCheckboxProducts);
      }
    }

    if (isUserSelected) {
      const eventProductID = _getProductIDFromVariantID(products, variant.id);
      embeddedSendEvent({
        name: EventNameProductButtonClick,
        action: EventActionSelect,
        primary,
        added: [],
        offered: products,
        variables: [
          {
            name: 'page',
            value: 'offer',
          },
          {
            name: 'type',
            value: 'variant',
          },
          {
            name: 'sp_product_id',
            value: eventProductID.toString(),
          },
          {
            name: 'sp_variant_id',
            value: variant.id.toString(),
          },
        ],
      });
    }
  };

  const addToCartWrapper = async (selectedVariant, productId, productTitle) => {
    const { id, off, calculatedDiscountFloat, title } = selectedVariant;
    if (isProductButtonActionGoToPage) {
      const product = _getProductFromVariantID(products, id);
      goToProductPage(product.handle);
      return;
    }

    const properties = getMonkDiscountProperties({
      isDiscountDisabled: calculatedDiscountFloat <= 0,
      off,
      offerType,
      properties: {},
      discountQuantity,
      isDiscountQuantityRestricted,
      cartDiscountText: props.theme.cartDiscountText,
    });

    props.addToCart({
      mrOfferID,
      variant: selectedVariant,
      properties,
      isEmbedded: true,
      offerType,
      isProductPage,
      primary,
      variantsWithQuantity,
      isBottomBarButtonActionGoToCart,
      theme: props.theme,
      productId,
      variantId: id,
      productTitle,
      variantTitle: title,
      widgetID,
    });
  };

  const productActionButtonStyles = getProductActionButtonStyles(
    props.theme,
    isMobile,
  );

  const getVariantFillerWidth = (variantFiller, variantFillerStyles) => {
    if (!variantFiller || !variantFillerStyles) return 0;

    const { type, fontFamily, fontWeight, padding } = variantFillerStyles;
    let { fontSize, mobileFontSize } = variantFillerStyles;

    if (Number.isNaN(Number(fontSize))) fontSize = 0;
    if (Number.isNaN(Number(mobileFontSize))) mobileFontSize = 0;

    const maxFontSize = Math.max(Number(fontSize), Number(mobileFontSize));

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    context.font = `${fontWeight} ${maxFontSize}px ${
      fontFamily === 'inherit'
        ? getComputedStyle(document.body).fontFamily
        : fontFamily
    }`;

    const textWidth = context.measureText(variantFiller).width;
    const paddingArr = getSeparatedStyleValueArr(padding);
    const borderWidth = type === 'button' ? 2 : 0;

    let fillerWidth =
      textWidth + Number(paddingArr[1]) + Number(paddingArr[3]) + borderWidth;

    // Adjusting addedToCartText width with iconWidth and gap
    if (type === 'text') {
      fillerWidth = fillerWidth + maxFontSize + 5;
    }

    return fillerWidth;
  };

  const getVariantFiller = useCallback(
    () => {
      const variantStyles = {
        type: 'button',
        fontSize: productActionButtonStyles.variantSelectorFontSize,
        mobileFontSize: productActionButtonStyles.variantSelectorMobileFontSize,
        fontFamily: productActionButtonStyles.variantSelectorFontFamily,
        fontWeight: productActionButtonStyles.variantSelectorFontWeight,
        fontStyle: productActionButtonStyles.variantSelectorFontStyle,
        padding: productActionButtonStyles.variantSelectorPadding,
        margin: productActionButtonStyles.variantSelectorMargin,
      };
      const atcStyles = {
        type: 'button',
        fontSize: productActionButtonStyles.atcButtonFontSize,
        mobileFontSize: productActionButtonStyles.atcButtonMobileFontSize,
        fontFamily: productActionButtonStyles.atcButtonFontFamily,
        fontWeight: productActionButtonStyles.atcButtonFontWeight,
        fontStyle: productActionButtonStyles.atcButtonFontStyle,
        padding: productActionButtonStyles.atcButtonPadding,
        margin: productActionButtonStyles.atcButtonMargin,
      };
      const addedStyles = {
        type: 'text',
        fontSize: productActionButtonStyles.addedToCartFontSize,
        mobileFontSize: productActionButtonStyles.addedToCartMobileFontSize,
        fontFamily: productActionButtonStyles.addedToCartFontFamily,
        fontWeight: productActionButtonStyles.addedToCartFontWeight,
        fontStyle: productActionButtonStyles.addedToCartFontStyle,
        padding: productActionButtonStyles.addedToCartPadding,
        margin: productActionButtonStyles.addedToCartMargin,
      };

      const fillerCandidates = [];

      let maxVaraintText = '';
      for (let i = 0; i < products.length; i += 1) {
        for (let j = 0; j < products[i].variants.length; j += 1) {
          if (
            maxVaraintText.length < products[i].variants[j].title.length &&
            products[i].variants[j].title !== 'Default Title'
          ) {
            maxVaraintText = products[i].variants[j].title;
          }
        }
      }
      fillerCandidates.push([
        maxVaraintText,
        {
          ...variantStyles,
          width: getVariantFillerWidth(maxVaraintText, variantStyles),
        },
      ]);

      if (layoutType !== 'checkbox') {
        fillerCandidates.push([
          props.theme.productButtonText,
          {
            ...atcStyles,
            width: getVariantFillerWidth(
              props.theme.productButtonText,
              atcStyles,
            ),
          },
        ]);

        if (isNumberOfOfferedProductsRestricted) {
          fillerCandidates.push([
            props.theme.addedToCartText,
            {
              ...addedStyles,
              width: getVariantFillerWidth(
                props.theme.addedToCartText,
                addedStyles,
              ),
            },
          ]);
        }
      }

      let maxFillerWidth = 0;
      if (fillerCandidates.length !== 0) {
        maxFillerWidth = Math.max(
          ...fillerCandidates.map(candidate => candidate[1].width),
        );
      }

      return (
        fillerCandidates.find(
          candidate => candidate[1].width === maxFillerWidth,
        ) || []
      );
    },
    [mobileLayoutType, desktopLayoutType, props.theme],
  );

  const [variantFiller, variantFillerStyles] = getVariantFiller();

  const onGoToProductPage = idx => {
    embeddedSendEvent({
      name: EventNameOfferProductPage,
      action: EventActionOpen,
      primary,
      added: [],
      offered: products,
      variables: [
        {
          name: 'page',
          value: 'offer',
        },
        {
          name: 'type',
          value:
            products[idx].variants.length > 0 &&
            products[idx].title !== 'Default Title'
              ? EventVariableVariant
              : EventVariableDefault,
        },
        {
          name: 'sp_product_id',
          value: products[idx].id.toString(),
        },
      ],
    });

    goToProductPage(products[idx].handle);
  };

  const getProductIndexesFromCart = ({ product }) => {
    const indexes = [];
    for (let i = 0; i < cart.length; i += 1) {
      if (cart[i].sp_variant_id === product.variant_id) {
        indexes.push(i);
      }
    }
    return indexes;
  };
  const quantityCalculationInCaseOfReserved = current => {
    // finding indexes in cart where the current product is present
    const indexInCart = getProductIndexesFromCart({
      product: current,
    });

    if (indexInCart.length > 0) {
      let totalReservedQuantity = 0;

      // calculating total reserved quantity by adding values from reservedItemQuantity flag we get in offer.
      indexInCart.forEach(elem => {
        if (reservedItemQuantity[elem]) {
          totalReservedQuantity += reservedItemQuantity[elem];
        }
      });

      const addedQuantity = current.quantity - totalReservedQuantity;

      if (addedQuantity > 0) {
        if (addedQuantity > discountQuantity && isDiscountQuantityRestricted) {
          return discountQuantity;
        }
        return addedQuantity;
      }
    }
    return 0;
  };

  const quantityCalculation = current => {
    if (isConsiderReservedQuantity) {
      const quantityInCaseOfReserved = quantityCalculationInCaseOfReserved(
        current,
      );
      return quantityInCaseOfReserved;
    }
    return current.quantity > discountQuantity && isDiscountQuantityRestricted
      ? discountQuantity
      : current.quantity;
  };

  const filterProductAdditions = additions => {
    const formattedProductAdditions = [];

    const getProductIndex = elem =>
      formattedProductAdditions.findIndex(
        item => item.variant_id === elem.variant_id,
      );
    additions.forEach(elem => {
      if (getProductIndex(elem) === -1) {
        formattedProductAdditions.push({ ...elem });
      } else {
        formattedProductAdditions[getProductIndex(elem)].quantity +=
          elem.quantity;
      }
    });
    return formattedProductAdditions;
  };

  const getOfferLevelQuantityAdded = () => {
    const formattedProductAdditions = filterProductAdditions(productAdditions);

    let totalQuantity = formattedProductAdditions.reduce(
      (total, current) => total + quantityCalculation(current),
      0,
    );

    if (
      !isConsiderReservedQuantity &&
      _isPrimaryAndOfferedSame({ productAdditions, primary })
    ) {
      totalQuantity -= 1;
    }

    return totalQuantity > offerLevelDiscountQuantity
      ? offerLevelDiscountQuantity
      : totalQuantity;
  };

  // handle on product image click
  const onProductImageClick = idx => {
    if (isAdmin) return;

    if (!isProductImageClickGoToPage) {
      return;
    }

    if (
      [
        'knack-roularta.myshopify.com',
        'libelle-lekker-roularta.myshopify.com',
        'zenmindstore.myshopify.com',
        'bfnky.myshopify.com',
        'test-sub-rmg.myshopify.com',
      ].includes(shopURL)
    ) {
      if (offerType === 'freegift') {
        return;
      }
    }

    onGoToProductPage(idx);
  };

  const onProductTitleClick = idx => {
    if (isAdmin) return;

    if (!isProductTitleClickGoToPage) return;
    onGoToProductPage(idx);
  };

  const updatingQuantityBasedOnReservedItems = ({
    currProductAdditions,
    quantityAdded,
  }) => {
    let updatedQuantity = quantityAdded;
    for (let i = 0; i < currProductAdditions.length; i += 1) {
      for (let j = 0; j < cart.length; j += 1) {
        if (
          currProductAdditions[i].variant_id === cart[j].sp_variant_id &&
          reservedItemQuantity[j]
        ) {
          if (quantityAdded >= reservedItemQuantity[j]) {
            updatedQuantity -= reservedItemQuantity[j];
          } else {
            updatedQuantity = 0;
          }
        }
      }
    }
    return updatedQuantity;
  };

  // Get all data for rendering and updating product items and variants
  const getProductAndSelectedVariantData = useCallback(
    (product, selectedVariant) => {
      if (!product.originalIndex) {
        product.originalIndex = products.findIndex(
          item => item.id === product.id,
        );
      }

      // get Selected Image for variant
      const images =
        product.images && product.images.length > 0
          ? product.images
          : [{ src: 'https://via.placeholder.com/124x164' }];
      const selectedImageIndex =
        selectedVariant.image_index > -1 ? selectedVariant.image_index : 0;
      const selectedImage = images[selectedImageIndex];

      const showFree = isFreegiftOffer && !isShowPriceInFreeGift; // get if Show Free Text for freeGift offer

      const fbtProductPriceDetails = getFBTProductPriceDetails({
        productIndex: product.originalIndex,
        selectedVariant,
        checkedProductsInfo: fbtCheckedProductsInfo,
      });

      // get current product's additions
      const currProductAdditions = getProductAddtionsByProductIndex(
        product.originalIndex,
      );

      // get current product's added quantity in cart
      let quantityAdded = _getProductQuantity({
        productAdditions: currProductAdditions,
      });

      // since reserved items should be present in cart
      if (isConsiderReservedQuantity) {
        quantityAdded = updatingQuantityBasedOnReservedItems({
          currProductAdditions,
          quantityAdded,
        });
      }

      if (
        !isConsiderReservedQuantity &&
        _isPrimaryAndOfferedSame({
          productAdditions: currProductAdditions,
          primary,
        })
      ) {
        quantityAdded -= 1;
      }

      const offerLevelQuantityAdded = getOfferLevelQuantityAdded();

      const isCheckboxLayout =
        (isMobile && mobileLayoutType === 'checkbox') ||
        (!isMobile && desktopLayoutType === 'checkbox');

      const isOfferQuantityAddedLimitReached = () => {
        if (isCheckboxLayout) {
          return (
            selectedCheckboxProducts.length + offerLevelQuantityAdded >=
            offerLevelDiscountQuantity
          );
        }
        return offerLevelQuantityAdded >= offerLevelDiscountQuantity;
      };

      // flag to check if the product level discount quantity limit is reached
      const isProductLevelDiscountQuantityLimitReached =
        isDiscountQuantityRestricted && quantityAdded >= discountQuantity;

      const isOfferLevelDiscountQuantityLimitReached =
        isNumberOfOfferedProductsRestricted &&
        isOfferQuantityAddedLimitReached();

      // get if discount doesn't apply/exist for selected variant
      const isDiscountDisabled = selectedVariant.calculatedDiscountFloat <= 0;

      const isProductAdditionEnabled =
        !isOfferLevelDiscountQuantityLimitReached &&
        !isProductLevelDiscountQuantityLimitReached;

      // we double check isProductAdditionEnabled because in case when discountLevelQuantity is reached
      // and some quantity is added the because isProductAdditionEnabled gets false and atc button doesn't display
      // but we want to show it in added state therefore we use this condition

      const showATCButton =
        !(!isProductAdditionEnabled && !quantityAdded) ||
        isPdpFgWithRepetetionOfRules;

      // if offered product has multiple variants
      const hasMultipleVariants =
        product.variants.length > 0 &&
        product.variants[0].title !== 'Default Title';

      // is showVariantSelector for product
      const showVariantSelector =
        hasMultipleVariants &&
        !isVariantSelectionDisabled &&
        ((layoutType !== 'checkbox' ? isProductAdditionEnabled : true) ||
          isPdpFgWithRepetetionOfRules);

      const variantAtcStatus =
        atcStatus.spVariantID === selectedVariant.id &&
        atcStatus.widgetID === widgetID
          ? atcStatus
          : {
              adding: false,
              added: false,
            };

      // get added variants to show as chips
      const addedVariants = _getAddedVariantsToDisplay({
        productAdditions: currProductAdditions,
        primary,
      });

      const addedVariantsPrefix = addedVariantsPrefixMap[shopURL];

      // get if added variant chips are shown with the added quantity
      const showAddedVariantsChipQuantity =
        hasMultipleVariants &&
        !isSelectedVariantItemsDisabled &&
        discountQuantity > 1;

      // get if added variant chips are shown
      const showAddedVariantsChip =
        hasMultipleVariants &&
        !isSelectedVariantItemsDisabled &&
        quantityAdded > 0;

      const isMaxQtyAdded =
        (isProductLevelDiscountQuantityLimitReached ||
          isOfferLevelDiscountQuantityLimitReached) &&
        quantityAdded > 0;

      const handleVariantChange = e => {
        e.preventDefault();
        e.stopPropagation();
        const selectedOption = e.target.value;
        let newSelectedVariant = {};

        for (let i = 0; i < product.variants.length; i += 1) {
          if (product.variants[i].title === selectedOption) {
            newSelectedVariant = product.variants[i];
            break;
          }
        }
        onSelectVariant(product.originalIndex, newSelectedVariant, true);
      };

      const onAddClick = async e => {
        if (e && e.stopPropagation) {
          e.stopPropagation();
        }
        _onAddClick({
          product,
          selectedVariant,
          isProductButtonActionGoToPage,
          goToProductPage,
          hasMultipleVariants,
          addToCart: addToCartWrapper,
          getProductQuantity: () =>
            _getProductQuantity({ productAdditions: currProductAdditions }),
          productEvent,
        });
      };

      // crossell tile layout
      const onProductAddonTileSelection = isSelected => {
        if (isSelected) return setSelectedCheckboxProducts([]);

        return setSelectedCheckboxProducts([
          { ...product, variant: product.variants[0] },
        ]);
      };

      const addOnProductHandler = () =>
        onAddProductToAddon(product, selectedVariant);

      const productImageClickHandler = () => {
        if (
          isToggleCheckboxOnImageClick &&
          offerType === 'crossell' &&
          ((isMobile && mobileLayoutType === 'checkbox') ||
            (!isMobile && desktopLayoutType === 'checkbox'))
        )
          addOnProductHandler();
        else onProductImageClick(product.originalIndex);
      };

      const isDisableProductAddition =
        !isRepeatRules && !isProductAdditionEnabled;

      return {
        selectedImage,
        offerPrice: showFree ? freeText : selectedVariant.offer_price,
        price: selectedVariant.price,
        off: selectedVariant.off,
        isDiscountDisabled,
        showFree,
        quantityAdded: isProductLevelDiscountQuantityLimitReached
          ? discountQuantity
          : quantityAdded,
        hasMultipleVariants,
        showVariantSelector,
        showATCButton,
        addedVariants,
        addedVariantsPrefix,
        showAddedVariantsChip,
        showAddedVariantsChipQuantity,
        withoutFirstAction: isVariantSelectionDisabled, // this means first action (VS) is hidden by user.
        variantFiller,
        variantFillerStyles,
        variantOptions: product.variants.map(variant => variant.title),
        productAdditions: currProductAdditions,
        onProductImageClick: productImageClickHandler,
        onProductTitleClick: () => onProductTitleClick(product.originalIndex),
        handleVariantChange,
        onAddClick,
        onAddProductToAddon: addOnProductHandler,
        getIndexInSelectedCheckboxProducts,
        onProductAddonTileSelection,
        isProductAdditionEnabled,
        variantAtcStatus,
        isMaxQtyAdded,
        // FBT
        fbtProductPriceDetails,
        isSkipATCIntermediateState: isPdpFgWithRepetetionOfRules,
        isDisableProductAddition,
      };
    },
    [
      JSON.stringify(displayProducts),
      JSON.stringify(selectedVariantInfo),
      JSON.stringify(productAdditions),
      JSON.stringify(fbtCheckedProductsInfo),
      atcStatus,
      JSON.stringify(selectedCheckboxProducts),
    ],
  );

  const updateDisplayProductOnHideProductFlag = () => {
    const filtered = products
      .map((product, i) => ({ ...product, originalIndex: i }))
      .filter((_, i) => {
        const additions = getProductAddtionsByProductIndex(i);
        let qty = 0;

        if (isConsiderReservedQuantity) {
          const formattedProductAdditions = filterProductAdditions(additions);
          formattedProductAdditions.forEach(elem => {
            const quantityIncaseOfReserved = quantityCalculationInCaseOfReserved(
              elem,
            );
            qty += quantityIncaseOfReserved;
          });
        } else {
          qty = _getProductQuantity({
            productAdditions: additions,
          });

          if (
            _isPrimaryAndOfferedSame({ productAdditions: additions, primary })
          ) {
            qty -= 1;
          }
        }
        return isDiscountQuantityRestricted ? qty < discountQuantity : qty < 1;
      });
    setDisplayProducts(filtered);
  };

  const addVariantsOfProductsToHide = () => {
    if (!isHideAfterProductAddition) {
      return;
    }

    const ids = products.reduce((acc, product) => {
      const { variants } = product;
      const variantIDs = variants.reduce((curr, variant) => {
        const { id } = variant;
        const quantity = getVariantQuantityFromCart(cart, id);
        if (quantity > 0) {
          return curr.concat(id);
        }
        return curr;
      }, []);
      return acc.concat(variantIDs);
    }, []);

    if (ids.length > 0) {
      addVariantIdsOfProductsToHide({ ids });
    }
  };

  const getVariantQuantity = (additions, variantid) =>
    _getVariantQuantity(additions, variantid);

  const getVariantQuantityFromCart = (cartVal, variantid) =>
    _getVariantQuantityFromCart(cartVal, variantid);

  const compareCartAndAddProductAdditions = () =>
    _compareCartAndAddProductAdditions({
      productAdditions,
      products,
      cart,
      getVariantQuantityFromCart,
      getVariantQuantity,
      balanceProductAddtions,
      widgetID,
    });

  const updateCarouselItemsToShow = () => {
    const mainContainerNode = mainContainerRef.current;
    const { width } = (mainContainerNode &&
      mainContainerNode.getBoundingClientRect()) || { width: 0 };
    logger.log('Monk carousel container width', width);
    setMainContainerWidth(width);

    let newCarouselItemsToShow = 0;

    if (layoutType === 'centered') {
      newCarouselItemsToShow = getNumberOfItemsForCentered(width);
      setCarouselItemsToShow(newCarouselItemsToShow);
      return;
    }

    if (layoutType === 'line-vertical') {
      newCarouselItemsToShow = getNumberOfItemsForLineVertical(width);
      setCarouselItemsToShow(newCarouselItemsToShow);
      return;
    }

    if (layoutType === 'compact') {
      newCarouselItemsToShow = getNumberOfItemsForCompact(width);
      setCarouselItemsToShow(newCarouselItemsToShow);
      return;
    }

    if (width > 1399) {
      newCarouselItemsToShow = 3;
    } else if (width > 1199) {
      newCarouselItemsToShow = 3;
    } else if (width > 991 && shopURL === 'thirdsheet.myshopify.com') {
      newCarouselItemsToShow = 3;
    } else if (width > 991) {
      newCarouselItemsToShow = 2.25;
    } else if (width > 767 && shopURL === 'thirdsheet.myshopify.com') {
      newCarouselItemsToShow = 3;
    } else if (width > 767) {
      newCarouselItemsToShow = 2;
    } else if (shopURL === 'mymogioielli.myshopify.com' && width > 575) {
      newCarouselItemsToShow = 1;
    } else if (width > 575) {
      newCarouselItemsToShow = 1.5;
    } else if (shopURL === 'theassemblyworkshop.myshopify.com') {
      newCarouselItemsToShow = 1.2;
    } else {
      newCarouselItemsToShow = 1;
    }

    setCarouselItemsToShow(newCarouselItemsToShow);
  };

  const getNumberOfItemsForCompact = width => {
    // for this store above 991 we need to display 4 items
    if (width > 991 && shopURL === 'schreiners-gardens.myshopify.com') {
      return 4;
    }

    if (width > 1199) {
      return 3;
    }

    if (width > 991) {
      return 3;
    }
    if (width > 767) {
      return 2;
    }
    if (width > 575) {
      return 1.5;
    }
    return 1;
  };

  const getNumberOfItemsForCentered = width => {
    if (shopURL === 'woox-online.myshopify.com' && width > 1199) {
      return 3;
    }

    if (shopURL === 'btstores-1571.myshopify.com') {
      if (width > 1199) {
        return 4;
      }
      if (width > 991) {
        return 3;
      }

      if (width > 767) {
        return 2;
      }
      return 1;
    }

    if (shopURL === 'miraggio-life.myshopify.com') {
      if (width < 480) {
        return 1.5;
      }
    }

    if (shopURL === 'theassemblyworkshop.myshopify.com' && width < 500) {
      return 1.2;
    }

    const imageWidth = parseInt(
      isMobile
        ? props.theme.productImageMobileWidth
        : props.theme.productImageWidth,
      10,
    );

    const atcWidth = Number(
      isMobile ? productButtonMobileWidth : productButtonWidth,
    );
    const vsWidth = Number(
      isMobile ? productVariantMobileWidth : productVariantWidth,
    );

    const productItemWidth = Math.max(imageWidth, atcWidth, vsWidth);

    // padding
    const paddingArr = getSeparatedStyleValueArr(
      isMobile
        ? props.theme.productItemMobilePadding
        : props.theme.productItemPadding,
    );

    // margin
    const marginArr = getSeparatedStyleValueArr(
      isMobile
        ? props.theme.productItemMobileMargin
        : props.theme.productItemMargin,
    );

    const productItemWidthWithSpacing =
      productItemWidth +
      paddingArr[1] +
      paddingArr[3] +
      marginArr[1] +
      marginArr[3];

    return +(width / productItemWidthWithSpacing).toFixed(2);
  };

  const getNumberOfItemsForLineVertical = width => {
    if (
      [
        'huntbikewheels-eu.myshopify.com',
        'huntbikewheels.myshopify.com',
        'huntbikewheels-us.myshopify.com',
        'huntbikewheels-cc.myshopify.com',
      ].includes(shopURL)
    ) {
      if (width > 767) {
        return 2;
      }
      if (width < 767) {
        return 1;
      }
      if (width > 1100) {
        return 2.5;
      }
    }

    if (shopURL === 'feather-moms.myshopify.com') {
      if (width >= 720) return 2;
      if (width < 720) return 1;
    }

    if (width > 1399) {
      return 5;
    }
    if (width > 1199) {
      return 4;
    }

    if (
      [
        'moroccanoil-admin.myshopify.com',
        'moil-de.myshopify.com',
        'moil-uk.myshopify.com',
        'moil-il.myshopify.com',
        'moroccanoil-ca.myshopify.com',
        'moroccanoil-admin-dev.myshopify.com',
      ].includes(shopURL)
    ) {
      if (width > 767) {
        return 3.5;
      }
    }

    if (width > 991) {
      return 3;
    }
    if (width > 767) {
      return 3;
    }
    if (width > 575) {
      return 2;
    }

    if (shopURL === 'quirky-perks-new.myshopify.com') {
      return 1;
    }

    if (width > 480) {
      return 1.5;
    }

    if (
      [
        'moroccanoil-admin.myshopify.com',
        'moil-de.myshopify.com',
        'moil-uk.myshopify.com',
        'moil-il.myshopify.com',
        'moroccanoil-ca.myshopify.com',
        'moroccanoil-admin-dev.myshopify.com',
      ].includes(shopURL)
    ) {
      return 1.4;
    }

    return 1;
  };

  const getHideOffer = () => {
    const isPrimaryPresentInCart = cart.some(
      elem => elem.sp_variant_id === primary.sp_variant_id,
    );

    // in case of pdp fg, if the triger isn't present in cart, we should ignore isHideAfterProductAddition flag.
    if (!isHideAfterProductAddition || (isPdpFg && !isPrimaryPresentInCart))
      return false;

    return (
      displayProducts.length < 1 ||
      (isNumberOfOfferedProductsRestricted &&
        getOfferLevelQuantityAdded() >= offerLevelDiscountQuantity)
    );
  };

  // FBT helpers
  const getFBTProductImages = ({ checkedProductsInfo }) =>
    products.reduce((acc, curr, idx) => {
      if (checkedProductsInfo[idx]) {
        const images =
          curr.images && curr.images.length > 0
            ? curr.images
            : [{ src: 'https://via.placeholder.com/124x164' }];
        const selectedImageIndex =
          selectedVariantInfo[idx].image_index > -1
            ? selectedVariantInfo[idx].image_index
            : 0;
        return acc.concat({
          src: images[selectedImageIndex].src,
        });
      }
      return acc;
    }, []);

  // Checks if all fbt products are selected and full discount will be applied
  const getIsFullDiscount = checkedProductsInfo =>
    checkedProductsInfo.reduce((acc, checked) => acc && checked, true);

  // checks if no offered products are selected(except this item product) and therefore discount is not applicable
  const getIsGlobalDiscountDisabled = checkedProductsInfo =>
    checkedProductsInfo.filter(checked => checked).length <= 1;

  const getFBTProductPriceDetails = ({
    productIndex,
    selectedVariant,
    checkedProductsInfo,
  }) => {
    if (!isFBTOffer) return {};

    const isChecked = checkedProductsInfo[productIndex];
    const isFullDiscount = getIsFullDiscount(checkedProductsInfo);
    const isGlobalDiscountDisabled = getIsGlobalDiscountDisabled(
      checkedProductsInfo,
    );

    const getOfferPrice = () => {
      if (
        isGlobalDiscountDisabled ||
        (isFBTDiscountDisabled && isFBTPartialDiscountDisabled)
      )
        return selectedVariant.zeroDiscountSellingPrice;

      // if discount is enabled and all products are not checked we disable discount
      if (
        !isFBTDiscountDisabled &&
        !isFullDiscount &&
        isFBTPartialDiscountDisabled
      )
        return selectedVariant.zeroDiscountSellingPrice;

      // if partial discount is enabled and all products are checked we disable discount
      if (
        !isFBTPartialDiscountDisabled &&
        isFullDiscount &&
        isFBTDiscountDisabled
      )
        return selectedVariant.zeroDiscountSellingPrice;

      // if (!isChecked) return selectedVariant.zeroDiscountSellingPrice;
      if (!isFBTDiscountDisabled) return selectedVariant.offer_price;
      if (!isFBTPartialDiscountDisabled) return selectedVariant.offer_price;
      return selectedVariant.zeroDiscountSellingPrice;
    };

    const getIsDiscountDisabled = () => {
      if (isGlobalDiscountDisabled) return true;

      // if discount is enabled and all products are not checked we disable discount
      if (
        !isFBTDiscountDisabled &&
        !isFullDiscount &&
        isFBTPartialDiscountDisabled
      )
        return true;

      // if partial discount is enabled and all products are checked we disable discount
      if (
        !isFBTPartialDiscountDisabled &&
        isFullDiscount &&
        isFBTDiscountDisabled
      )
        return true;

      if (
        !isChecked &&
        (!isFBTDiscountDisabled || !isFBTPartialDiscountDisabled)
      )
        return true;

      if (!isFBTDiscountDisabled)
        return selectedVariant.calculatedDiscountFloat <= 0;
      if (!isFBTPartialDiscountDisabled)
        return selectedVariant.discounts.partial.calculatedDiscountFloat <= 0;

      return isFullDiscount || !isChecked
        ? selectedVariant.calculatedDiscountFloat <= 0
        : selectedVariant.discounts.partial.calculatedDiscountFloat <= 0;
    };

    return {
      offerPrice: getOfferPrice(),
      price: selectedVariant.price,
      off:
        isFullDiscount || !isChecked
          ? selectedVariant.off
          : selectedVariant.discounts.partial.off,
      isDiscountDisabled: getIsDiscountDisabled(),
    };
  };

  const calculateFBTTotal = ({ checkedProductsInfo }) => {
    const isFullDiscount = getIsFullDiscount(checkedProductsInfo);
    const isGlobalDiscountDisabled = getIsGlobalDiscountDisabled(
      checkedProductsInfo,
    );

    const calculatedTotal = products.reduce(
      (acc, curr, itemIndex) => {
        if (checkedProductsInfo[itemIndex]) {
          acc.priceFloat += isGlobalDiscountDisabled
            ? selectedVariantInfo[itemIndex].zeroDiscountSellingPriceFloat
            : selectedVariantInfo[itemIndex].priceFloat;

          const getOfferPriceFloat = () => {
            if (isFBTDiscountDisabled && isFBTPartialDiscountDisabled)
              return roundOff(
                selectedVariantInfo[itemIndex].zeroDiscountSellingPriceFloat,
              );

            // if discount is enabled and all products are not checked we disable discount
            if (
              !isFBTDiscountDisabled &&
              !isFullDiscount &&
              isFBTPartialDiscountDisabled
            )
              return roundOff(
                selectedVariantInfo[itemIndex].zeroDiscountSellingPriceFloat,
              );

            // if partial discount is enabled and all products are checked we disable discount
            if (
              !isFBTPartialDiscountDisabled &&
              isFullDiscount &&
              isFBTDiscountDisabled
            )
              return roundOff(
                selectedVariantInfo[itemIndex].zeroDiscountSellingPriceFloat,
              );

            if (!isFBTDiscountDisabled && isFullDiscount)
              return roundOff(
                selectedVariantInfo[itemIndex].offer_price_float,
                priceRoundType,
              );
            if (!isFBTPartialDiscountDisabled && !isFullDiscount)
              return roundOff(
                selectedVariantInfo[itemIndex].discounts.partial
                  .offer_price_float,
                priceRoundType,
              );
            return roundOff(
              selectedVariantInfo[itemIndex].zeroDiscountSellingPriceFloat,
            );
          };
          const getIsDisplayDiscount = () => {
            if (isGlobalDiscountDisabled) return false;

            // if discount is enabled and all products are not checked we disable discount
            if (
              !isFBTDiscountDisabled &&
              !isFullDiscount &&
              isFBTPartialDiscountDisabled
            )
              return false;

            // if partial discount is enabled and all products are checked we disable discount
            if (
              !isFBTPartialDiscountDisabled &&
              isFullDiscount &&
              isFBTDiscountDisabled
            )
              return false;

            if (!isFBTDiscountDisabled && isFullDiscount)
              return selectedVariantInfo[itemIndex].calculatedDiscountFloat > 0;
            if (!isFBTPartialDiscountDisabled && !isFullDiscount)
              return (
                selectedVariantInfo[itemIndex].discounts.partial
                  .calculatedDiscountFloat > 0
              );
            return isFullDiscount
              ? selectedVariantInfo[itemIndex].calculatedDiscountFloat > 0
              : selectedVariantInfo[itemIndex].discounts.partial
                  .calculatedDiscountFloat > 0;
          };
          acc.offerPriceFloat += getOfferPriceFloat();

          acc.discountFloat += isFullDiscount
            ? roundOff(
                selectedVariantInfo[itemIndex].calculatedDiscountFloat,
                discountRoundType,
              )
            : roundOff(
                selectedVariantInfo[itemIndex].discounts.partial
                  .calculatedDiscountFloat,
                discountRoundType,
              );

          acc.displayDiscount = acc.displayDiscount || getIsDisplayDiscount();
        }
        return acc;
      },
      {
        priceFloat: 0,
        offerPriceFloat: 0,
        discountFloat: 0,
        displayDiscount: false,
      },
    );

    if (discountType === 'flat') {
      let discountValueToCalc = isGlobalDiscountDisabled ? 0 : fbtDiscountValue;
      let isDiscountDisabled = isFBTDiscountDisabled;
      if (!isGlobalDiscountDisabled && !isFullDiscount) {
        discountValueToCalc = fbtPartialDiscountValue;
        isDiscountDisabled = isFBTPartialDiscountDisabled;
      }

      const flatDiscount = calculateDiscountAdmin(
        calculatedTotal.priceFloat,
        0,
        '',
        0,
        0,
        discountValueToCalc,
        isDiscountDisabled,
      );

      calculatedTotal.priceFloat = flatDiscount.priceFloat;
      calculatedTotal.offerPriceFloat = flatDiscount.offerPriceFloat;
      calculatedTotal.discountFloat = flatDiscount.discountFloat;
      calculatedTotal.displayDiscount = flatDiscount.discountFloat > 0;
    }

    return calculatedTotal;
  };

  const getFBTDisplayTotalValues = calculatedTotal => {
    let totalPrice = getAmountDisplay({
      currencyFormat,
      currencySymbol,
      amount: calculatedTotal.priceFloat,
      decimalSymbol,
      thousandSeparator,
    });

    if (isAdmin) {
      totalPrice = totalPrice.replace(
        roundTo2String(calculatedTotal.priceFloat),
        'XX.XX',
      );
    }

    let offerPrice = getAmountDisplay({
      currencyFormat,
      currencySymbol,
      amount: calculatedTotal.offerPriceFloat,
      decimalSymbol,
      thousandSeparator,
    });

    if (isAdmin) {
      offerPrice = offerPrice.replace(
        roundTo2String(calculatedTotal.offerPriceFloat),
        'XX.XX',
      );
    }

    let discountValue = calculateDiscountValue({
      price: calculatedTotal.priceFloat,
      offerPrice: calculatedTotal.offerPriceFloat,
      discountType,
    });

    discountValue = roundOff(discountValue, discountRoundType);

    let discount = getDiscountDisplay({
      discount: calculatedTotal.discountFloat,
      discountValue,
      discountType,
      currencyFormat,
      currencySymbol,
      productDiscountText: props.theme.productDiscountText,
    });

    if (isAdmin) {
      discount = 'XX.XX';
    }

    // add this support globally for all offers
    if (shopURL === 'suprfit-fitness-equipment.myshopify.com') {
      totalPrice = totalPrice.replace('.', ',');
      offerPrice = offerPrice.replace('.', ',');
      discount = discount.replace('.', ',');
    }

    if (shopURL === 'wildnest-camper.myshopify.com') {
      totalPrice = totalPrice.replace('.', ',');
      offerPrice = offerPrice.replace('.', ',');
      discount = discount.replace('.', ',');
    }

    return {
      totalPrice,
      offerPrice,
      discount,
      displayDiscount: calculatedTotal.displayDiscount,
    };
  };

  const handleFBTCheckboxchange = itemIndex => {
    if (!itemIndex || itemIndex === 0) return;

    setFBTCheckedProductsInfo(prev =>
      prev.map((val, i) => (i === itemIndex ? !val : val)),
    );
  };

  const onAddFBTProducts = async () => {
    if (isAdmin) {
      return;
    }
    setIsFBTActionLoading(true);

    const totalValues = getFBTDisplayTotalValues(fbtTotal);
    const variantsToAdd = await _getBundleOrAddonOrFBTVariantsToAdd({
      products: products.filter((item, idx) => fbtCheckedProductsInfo[idx]),
      selectedVariantInfo: selectedVariantInfo.filter(
        (item, idx) => fbtCheckedProductsInfo[idx],
      ),
      isDiscountDisabled: !totalValues.displayDiscount,
      discountQuantity: 1,
      isDiscountQuantityRestricted: true,
      theme: props.theme,
      offerType: 'fbt',
      isSkipIfAlreadyInCart: true,
    });

    if (variantsToAdd.length > 0) {
      await addOfferedVariantsToCart(variantsToAdd);
      for (let i = 0; i < variantsToAdd.length; i += 1) {
        // eslint-disable-next-line
        const { product_id, variant_id } = variantsToAdd[i];
        addToCartSuccessEvent({
          mrOfferID,
          product_id,
          variant_id,
          isEmbedded: true,
          isProductPage: true,
          theme: props.theme,
          offerType: 'fbt',
          widgetID,
        });
      }
    }
    setIsFBTActionLoading(false);
    setIsFBTProductsAdded(true);

    setTimeout(() => {
      setIsFBTProductsAdded(false);
    }, 2000);

    const {
      isProductButtonActionGoToCart,
      isRefreshPageAfterProductAddition,
    } = props.theme;

    if (isProductButtonActionGoToCart) {
      goToCart({ isPopup: false, isRefreshPageAfterProductAddition });
      if (isRefreshPageAfterProductAddition) {
        return;
      }
    }

    if (shopURL === 'the-slate-plate.myshopify.com') {
      window.scrollTo(0, 0);
      reloadPage();
    }
    if (shopURL === 'drocto.myshopify.com') {
      reloadPage();
    }
    if (shopURL === 'seevo-in.myshopify.com') {
      reloadPage();
    }
    if (shopURL === 'thetrampoline.myshopify.com') {
      reloadPage();
    }

    if (isRefreshPageAfterProductAddition) {
      logger.log('refresh initiated line 1606');
      reloadPage();
    }
  };
  // FBT helpers

  useEffect(
    () => {
      updateCarouselItemsToShow();
    },
    [
      isAdminMobileTab,
      adminShowLargePreview,
      mainContainerRef,
      displayProducts.length,
      // JSON.stringify(props.theme),
      mobileLayoutType,
      desktopLayoutType,
      props.theme.productImageWidth,
      props.theme.productImageMobileWidth,
      props.theme.productButtonWidth,
      props.theme.productButtonMobileWidth,
      props.theme.productVariantWidth,
      props.theme.productVariantMobileWidth,
    ],
  );

  useEffect(() => {
    updateCarouselItemsToShow();
    embeddedSendEvent({
      name: EventNameOfferPageLaunch,
      primary,
      action: isProductPage ? EventActionProductPage : EventActionCartPage,
      offered: products,
    });

    window.addEventListener('resize', updateCarouselItemsToShow);
    return () => {
      window.removeEventListener('resize', updateCarouselItemsToShow);
    };
  }, []);

  useEffect(
    () => {
      const newSelectedVariantInfo = products.reduce(
        (acc, _, idx) => acc.concat(products[idx].variants[0]),
        [],
      );

      setSelectedVariantInfo(newSelectedVariantInfo);
      setDisplayProducts(
        products.map((product, i) => ({ ...product, originalIndex: i })),
      );
      if (offerType === 'crossell' && layoutType === 'tiles') {
        setSelectedCheckboxProducts([]);
      }
      if (isFBTOffer) setFBTCheckedProductsInfo(products.map(() => true));
    },
    [JSON.stringify(products)],
  );

  useEffect(
    () => {
      if (isHideAfterProductAddition) {
        if (variantIDsOfProductsToHide.length > 0) {
          updateDisplayProductOnHideProductFlag();
        }
      }
    },
    [
      JSON.stringify(variantIDsOfProductsToHide),
      JSON.stringify(productAdditions),
    ],
  );

  useEffect(
    () => {
      compareCartAndAddProductAdditions();
      addVariantsOfProductsToHide();
    },
    [JSON.stringify(cart)],
  );

  useEffect(
    () => {
      if (isFBTOffer) {
        setFBTImagesArray(
          getFBTProductImages({ checkedProductsInfo: fbtCheckedProductsInfo }),
        );
        setFBTTotal(
          calculateFBTTotal({ checkedProductsInfo: fbtCheckedProductsInfo }),
        );
      }
    },
    [fbtCheckedProductsInfo, selectedVariantInfo],
  );

  const embeddedWidgetStyles = {
    fontFamily: widgetFontFamily,
    backgroundColor: widgetBackgroundColor,
    border:
      isWidgetBordered && `${widgetBorderWidth}px solid ${widgetBorderColor}`,
    borderRadius: widgetBorderRadius,
    padding: isMobile ? widgetMobilePadding : widgetPadding,
    margin: isMobile ? widgetMobileMargin : widgetMargin,
    mobilePadding: widgetMobilePadding,
    mobileMargin: widgetMobileMargin,
  };

  const getContainerWidthForProductActionBtnLayoutChange = () => {
    if (shopURL === 'partylite-uk.myshopify.com') {
      return 550;
    }
    return 500;
  }

  logger.log('Monk display products', displayProducts);
  logger.log('Monk offered Products', products);
  const commonProps = {
    ref: mainContainerRef,
    containerWidth: mainContainerWidth,
    isMobile,
    products,
    selectedVariantInfo,
    displayProducts,
    embeddedWidgetStyles,
    productActionButtonStyles,
    priceDetailStyles: getPriceDetailStyles(isMobile, props.theme),
    getProductAndSelectedVariantData,
  };

  const isRenderProductsInCarousel = isMobile
    ? isEmbeddedMobileCarouselEnabled
    : isEmbeddedCarouselEnabled;

  logger.log('Monk carouselItemsToShow', carouselItemsToShow);
  const crossSellProps = {
    withCarousel: isRenderProductsInCarousel,
    carouselItemsToShow,
    onAddProductAddonsToCart,
    offerLevelQuantityAdded: getOfferLevelQuantityAdded(),
    offerLevelDiscountQuantity,
    isCarouselArrowsAlwaysVisible,
    ...commonProps,
    ...restProps,
    theme: getConstrainedTheme(),
  };

  const lineLayoutProps = {
    containerWidthForProductActionBtnLayoutChange: getContainerWidthForProductActionBtnLayoutChange(),
  }

  const fbtProps = {
    isFBTActionLoading,
    isFBTProductsAdded,
    checkedProductsInfo: fbtCheckedProductsInfo,
    productImages: fbtImagesArray,
    isGlobalDiscountDisabled: getIsGlobalDiscountDisabled(
      fbtCheckedProductsInfo,
    ),
    totalValues: getFBTDisplayTotalValues(fbtTotal),
    handleFBTCheckboxchange,
    onAddFBTProducts,
    ...commonProps,
    ...restProps,
  };

  if (getHideOffer()) {
    return null;
  }

  if (offerType === 'crossell' || isFreegiftOffer) {
    if (layoutType === 'compact') {
      return <CompactLayout {...crossSellProps} />;
    }
    if (layoutType === 'centered') {
      return <LargeImageLayout {...crossSellProps} />;
    }
    if (layoutType === 'line') {
      return <LineLayout {...crossSellProps} {...lineLayoutProps} />;
    }
    if (layoutType === 'line-vertical') {
      return <LineVerticalLayout {...crossSellProps} />;
    }
    if (layoutType === 'checkbox') {
      return (
        <CheckboxLayout
          isProductAddonActionLoading={isProductAddonActionLoading}
          isProductAddonAdded={isProductAddonAdded}
          {...crossSellProps}
        />
      );
    }
    if (layoutType === 'tiles') {
      return (
        <TileLayout
          // Tile Layout does not use display products
          products={products}
          selectedProducts={selectedCheckboxProducts}
          {...crossSellProps}
        />
      );
    }
  }

  if (isFBTOffer) {
    return <FBT {...fbtProps} />;
  }

  return 'Could not render mr embedded widget';
}

Embedded.propTypes = {
  isConsiderReservedQuantity: PropTypes.bool,
  reservedItemQuantity: PropTypes.object,
  isRepeatRules: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isAdminMobileTab: PropTypes.bool,
  offerType: PropTypes.string,
  theme: PropTypes.object,
  adminShowLargePreview: PropTypes.bool,
  mrOfferID: PropTypes.string,
  shopURL: PropTypes.string,
  products: PropTypes.array,
  calculateDiscountAdmin: PropTypes.func,
  offTextAdmin: PropTypes.string,
  triggerType: PropTypes.string,
  eventConstants: PropTypes.object,
  sendEvent: PropTypes.func,
  cart: PropTypes.array,
  primary: PropTypes.object,
  productAdditions: PropTypes.array,
  balanceProductAddtions: PropTypes.array,
  isHideAfterProductAddition: PropTypes.bool,
  variantIDsOfProductsToHide: PropTypes.array,
  addVariantIdsOfProductsToHide: PropTypes.func,
  discountQuantity: PropTypes.number,
  isDiscountQuantityRestricted: PropTypes.bool,
  isNumberOfOfferedProductsRestricted: PropTypes.bool,
  goToProductPage: PropTypes.func,
  goToCart: PropTypes.func,
  getMonkDiscountProperties: PropTypes.func,
  variantsWithQuantity: PropTypes.array,
  changeCartVartiantWithoutProperties: PropTypes.func,
  selectedCheckboxProducts: PropTypes.array,
  setSelectedCheckboxProducts: PropTypes.func,
  addProductAddonsToCart: PropTypes.func,
  currencyFormat: PropTypes.string,
  currencySymbol: PropTypes.string,
  decimalSymbol: PropTypes.string,
  thousandSeparator: PropTypes.string,
  discountType: PropTypes.string,
  discountRoundType: PropTypes.string,
  priceRoundType: PropTypes.string,
  fbtDiscountValue: PropTypes.string,
  fbtPartialDiscountValue: PropTypes.string,
  isFBTDiscountDisabled: PropTypes.bool,
  isFBTPartialDiscountDisabled: PropTypes.bool,
  addOfferedVariantsToCart: PropTypes.func,
  addToCartSuccessEvent: PropTypes.func,
  _compareCartAndAddProductAdditions: PropTypes.func,
  _getVariantQuantity: PropTypes.func,
  _getVariantQuantityFromCart: PropTypes.func,
  _getProductQuantity: PropTypes.func,
  _isPrimaryAndOfferedSame: PropTypes.func,
  _getProductAddtionsByProductIndex: PropTypes.func,
  _isVariantPresentInCart: PropTypes.func,
  _onAddClick: PropTypes.func,
  _productEvent: PropTypes.func,
  _getProductFromVariantID: PropTypes.func,
  _getLastAddition: PropTypes.func,
  _getLastAddedVariant: PropTypes.func,
  _getAddedVariantsToDisplay: PropTypes.func,
  _getProductIDFromVariantID: PropTypes.func,
  _getBundleOrAddonOrFBTVariantsToAdd: PropTypes.func,
  offerLevelDiscountQuantity: PropTypes.number,
  atcStatus: PropTypes.object,
  dispatch: PropTypes.func,
  addToCart: PropTypes.func,
  widgetID: PropTypes.string,
};

const getProductActionButtonStyles = (theme, isMobile) => ({
  atcButtonText: theme.productButtonText,
  atcButtonTextColor: theme.embeddedProductButtonTextColor,
  atcButtonStyle: theme.embeddedProductButtonStyle,
  atcButtonBackgroundColor: theme.productButtonColor,
  atcButtonBorderColor: theme.productButtonColor,
  atcButtonBorderRadius: theme.productButtonBorderRadius,
  atcButtonFontSize: isMobile
    ? theme.productButtonMobileFontSize
    : theme.productButtonFontSize,
  atcButtonMobileFontSize: theme.productButtonMobileFontSize,
  atcButtonFontStyle: theme.productButtonFontStyle,
  atcButtonFontWeight: theme.productButtonFontWeight,
  atcButtonTextDecoration: theme.productButtonTextDecoration,
  atcButtonTextAlignment: theme.productButtonTextAlignment,
  atcButtonFontFamily: theme.productButtonFontFamily,
  atcButtonPadding: theme.productButtonPadding,
  atcButtonMargin: theme.productButtonMargin,
  atcButtonHeight: isMobile
    ? theme.productButtonMobileHeight
    : theme.productButtonHeight,
  atcButtonWidth: isMobile
    ? theme.productButtonMobileWidth
    : theme.productButtonWidth,
  atcButtonWidthStyle: theme.productButtonWidthStyle,
  atcProductAddingText: theme.atcProductAddingText,
  atcProductAddedText: theme.atcProductAddedText,
  quantitySelectorTextColor: theme.quantityButtonTextColor,
  quantitySelectorStyle: theme.quantityButtonStyle,
  quantitySelectorBackgroundColor: theme.quantityButtonColor,
  quantitySelectorBorderColor: theme.quantityButtonColor,
  quantitySelectorBorderRadius: theme.quantityButtonBorderRadius,
  quantitySelectorFontSize: isMobile
    ? theme.quantityButtonMobileFontSize
    : theme.quantityButtonFontSize,
  quantitySelectorMobileFontSize: theme.quantityButtonMobileFontSize,
  quantitySelectorFontStyle: theme.quantityButtonFontStyle,
  quantitySelectorFontWeight: theme.quantityButtonFontWeight,
  quantitySelectorTextDecoration: theme.quantityButtonTextDecoration,
  quantitySelectorTextAlignment: theme.quantityButtonTextAlignment,
  quantitySelectorFontFamily: theme.quantityButtonFontFamily,
  quantitySelectorPadding: theme.quantityButtonPadding,
  quantitySelectorMargin: theme.quantityButtonMargin,
  quantitySelectorHeight: isMobile
    ? theme.quantityButtonMobileHeight
    : theme.quantityButtonHeight,
  // quantitySelectorWidth: isMobile
  //   ? theme.quantityButtonMobileWidth
  //   : theme.quantityButtonWidth,
  // quantitySelectorWidthStyle: theme.quantityButtonWidthStyle,
  variantSelectorTextColor: theme.productVariantTextColor,
  variantSelectorStyle: theme.productVariantStyle,
  variantSelectorBackgroundColor: theme.productVariantColor,
  variantSelectorBorderColor: theme.productVariantColor,
  variantSelectorBorderRadius: theme.productVariantBorderRadius,
  variantSelectorFontSize: isMobile
    ? theme.variantDropdownMobileFontSize
    : theme.variantDropdownFontSize,
  variantSelectorMobileFontSize: theme.variantDropdownMobileFontSize,
  variantSelectorFontStyle: theme.productVariantFontStyle,
  variantSelectorFontWeight: theme.productVariantFontWeight,
  variantSelectorTextDecoration: theme.productVariantTextDecoration,
  variantSelectorTextAlignment: isMobile
    ? theme.productVariantMobileTextAlignment
    : theme.productVariantTextAlignment,
  variantSelectorFontFamily: theme.productVariantFontFamily,
  variantSelectorPadding: isMobile
    ? theme.productVariantMobilePadding
    : theme.productVariantPadding,
  variantSelectorMargin: isMobile
    ? theme.productVariantMobileMargin
    : theme.productVariantMargin,
  variantSelectorHeight: isMobile
    ? theme.productVariantMobileHeight
    : theme.productVariantHeight,
  variantSelectorWidth: isMobile
    ? theme.productVariantMobileWidth
    : theme.productVariantWidth,
  variantSelectorWidthStyle: theme.productVariantWidthStyle,
  addedToCartText: theme.addedToCartText,
  addedToCartTextColor: theme.addedToCartTextColor,
  addedToCartFontSize: isMobile
    ? theme.addedToCartMobileFontSize
    : theme.addedToCartFontSize,
  addedToCartMobileFontSize: theme.addedToCartMobileFontSize,
  addedToCartFontStyle: theme.addedToCartFontStyle,
  addedToCartFontWeight: theme.addedToCartFontWeight,
  addedToCartTextDecoration: theme.addedToCartTextDecoration,
  addedToCartTextAlignment: theme.addedToCartTextAlignment,
  addedToCartFontFamily: theme.addedToCartFontFamily,
  addedToCartPadding: theme.addedToCartPadding,
  addedToCartMargin: theme.addedToCartMargin,
});

export const getPriceDetailStyles = (isMobile, theme) => ({
  offerPriceStyles: {
    color: theme.productPriceTextColor,
    fontSize: isMobile
      ? theme.productPriceMobileFontSize
      : theme.productPriceFontSize,
    mobileFontSize: theme.productPriceMobileFontSize,
    fontStyle: theme.productPriceFontStyle,
    fontWeight: theme.productPriceFontWeight,
    textDecoration: theme.productPriceTextDecoration,
  },
  priceStyles: {
    color: theme.productPriceTextColor,
    fontSize: isMobile
      ? theme.productPriceMobileFontSize
      : theme.productPriceFontSize,
    mobileFontSize: theme.productPriceMobileFontSize,
    fontStyle: 'normal',
    fontWeight: 'normal',
    textDecoration: 'none',
  },
  offStyles: {
    color: theme.productDiscountTextColor,
    fontSize: isMobile
      ? theme.productDiscountMobileFontSize
      : theme.productDiscountFontSize,
    mobileFontSize: theme.productDiscountMobileFontSize,
    fontStyle: theme.productDiscountFontStyle,
    fontWeight: theme.productDiscountFontWeight,
    textDecoration: theme.productDiscountTextDecoration,
  },
});
